import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
   Box,
   Typography,
   TextField,
   Button,
   List,
   ListItem,
   ListItemText,
   ListItemAvatar,
   Avatar,
   Badge,
   Paper,
   IconButton,
   CircularProgress,
   Chip,
   Divider,
   Menu,
   MenuItem,
   Tooltip,
   useTheme,
   useMediaQuery,
   Alert,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   InputAdornment,
} from '@mui/material';
import {
   Send as SendIcon,
   AttachFile as AttachFileIcon,
   EmojiEmotions as EmojiIcon,
   MoreVert as MoreVertIcon,
   Edit as EditIcon,
   Delete as DeleteIcon,
   Reply as ReplyIcon,
   ThumbUp as ThumbUpIcon,
   Favorite as FavoriteIcon,
   EmojiEmotions as LaughIcon,
   People as PeopleIcon,
} from '@mui/icons-material';
import { useWebSocketChat } from '../../contexts/WebSocketChatContext';
import { useUser } from '../../contexts/UserContext';
import { ChatMessage, ChatRoom } from '../../services/WebSocketService';
import onlineStatusService from '../../services/OnlineStatusService';
import OnlineIndicator from '../OnlineIndicator/OnlineIndicator';
import { formatDistanceToNow } from 'date-fns';
import webSocketService from '../../services/WebSocketService';

interface WebSocketChatViewProps {
   room: ChatRoom;
}

const WebSocketChatView: React.FC<WebSocketChatViewProps> = ({ room }) => {
   const { currentUser } = useUser();
   const {
      messages,
      sendMessage,
      editMessage,
      deleteMessage,
      reactToMessage,
      removeReaction,
      loadRoomMessages,
      connectionStatus,
      startTyping,
      stopTyping,
      typingUsers,
      loadingStates,
      isSendingMessage,
      isAddingReaction,
      isRemovingReaction,
      getUserOnlineStatus,
      getMultipleUsersOnlineStatus,
      isUserOnline,
   } = useWebSocketChat();

   const [newMessage, setNewMessage] = useState('');
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const messagesEndRef = useRef<HTMLDivElement>(null);

   // State for message sender avatars
   const [messageSenderAvatars, setMessageSenderAvatars] = useState<{
      [userId: string]: string | null;
   }>({});

   // State for chat room avatar
   const [chatRoomAvatar, setChatRoomAvatar] = useState<string | null>(null);

   // State for menu and participants modal
   const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
   const [participantsData, setParticipantsData] = useState<
      Array<{
         userId: string;
         displayName: string;
         email?: string;
         photoURL?: string;
      }>
   >([]);
   const [loadingParticipants, setLoadingParticipants] = useState(false);

   const menuOpen = Boolean(menuAnchorEl);

   const currentUserId = currentUser?.uid || '';

   const roomMessages = messages[room.id] || [];

   // Check if sending message in this room
   const isCurrentlySendingMessage = isSendingMessage(room.id);

   // Robust scroll to bottom function
   const scrollToBottom = useCallback((behavior: 'auto' | 'smooth' | 'instant' = 'smooth') => {
      if (messagesEndRef.current) {
         // Map 'instant' to 'auto' for scrollIntoView - ScrollBehavior only accepts 'auto' | 'smooth'
         const scrollBehavior: ScrollBehavior =
            behavior === 'instant' ? 'auto' : (behavior as ScrollBehavior);

         // First try scrollIntoView on the ref element
         messagesEndRef.current.scrollIntoView({
            behavior: scrollBehavior,
            block: 'end',
         });

         // Also try to find and scroll the parent scrollable container
         let parent = messagesEndRef.current.parentElement;
         while (parent) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            if (
               overflowY === 'auto' ||
               overflowY === 'scroll' ||
               parent.scrollHeight > parent.clientHeight
            ) {
               parent.scrollTop = parent.scrollHeight;
               break;
            }
            parent = parent.parentElement;
         }
      }
   }, []);

   // Load room messages when room changes
   useEffect(() => {
      if (room.id) {
         setLoading(true);
         loadRoomMessages(room.id)
            .then(() => setError(null))
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
      }
   }, [room.id, loadRoomMessages]);

   // Load message sender avatars
   useEffect(() => {
      const loadMessageSenderAvatars = async () => {
         // Get all unique user IDs from messages and room members
         const senderIds = roomMessages.map((msg) => msg.senderId);
         const allUserIds = Array.from(new Set([...senderIds, ...room.members]));

         const avatarPromises = allUserIds.map(async (userId) => {
            if (userId === 'system') return { userId, avatarUrl: null };

            try {
               const avatarUrl = await webSocketService.getMessageSenderAvatarUrl({
                  senderId: userId,
                  senderName: '',
                  text: '',
                  timestamp: new Date(),
                  roomId: room.id,
                  id: '',
               });
               return { userId, avatarUrl };
            } catch (error) {
               console.error(`Error loading avatar for user ${userId}:`, error);
               return { userId, avatarUrl: null };
            }
         });

         const avatarResults = await Promise.all(avatarPromises);
         const avatarMap: { [userId: string]: string | null } = {};
         avatarResults.forEach((result) => {
            avatarMap[result.userId] = result.avatarUrl;
         });

         setMessageSenderAvatars(avatarMap);
      };

      if (room.id) {
         loadMessageSenderAvatars();
      }
   }, [roomMessages, room.id, room.members]);

   // Load chat room avatar when room changes
   useEffect(() => {
      const loadChatRoomAvatar = async () => {
         try {
            const avatarUrl = await webSocketService.getChatRoomAvatarUrl(room, currentUserId);
            setChatRoomAvatar(avatarUrl);
         } catch (error) {
            console.error('Error loading chat room avatar:', error);
            setChatRoomAvatar(null);
         }
      };

      if (room.id) {
         loadChatRoomAvatar();
      }
   }, [room.id, room.isPrivate, room.members, currentUserId]);

   // Auto-scroll to bottom when new messages arrive
   useEffect(() => {
      if (roomMessages.length > 0) {
         const timeoutId = setTimeout(() => scrollToBottom('smooth'), 100);
         return () => clearTimeout(timeoutId);
      }
   }, [roomMessages.length, scrollToBottom]);

   // Auto-scroll when room changes and messages are loaded
   useEffect(() => {
      if (roomMessages.length > 0 && !loading) {
         // Immediate scroll when room changes
         scrollToBottom('instant');

         // Follow up with smooth scroll to ensure we're at bottom
         setTimeout(() => scrollToBottom('smooth'), 150);
         setTimeout(() => scrollToBottom('smooth'), 400);
      }
   }, [room.id, loading, roomMessages.length, scrollToBottom]);

   // Scroll when loading finishes
   useEffect(() => {
      if (!loading && roomMessages.length > 0) {
         setTimeout(() => scrollToBottom('instant'), 50);
      }
   }, [loading, scrollToBottom]);

   // Memoized messages for performance
   const memoizedMessages = useMemo(() => roomMessages, [roomMessages]);

   // Get the appropriate avatar URL for the chat header
   const getChatHeaderAvatar = useCallback(() => {
      // If we already have a loaded chat room avatar, use it
      if (chatRoomAvatar) {
         return chatRoomAvatar;
      }

      // If we already have a room avatar URL, use it
      if (room.avatarUrl) {
         return room.avatarUrl;
      }

      // For direct chats, try to get the other user's avatar from message senders
      if (room.isPrivate && room.members.length === 2) {
         const otherUserId = room.members.find((id) => id !== currentUserId);
         if (otherUserId && messageSenderAvatars[otherUserId]) {
            return messageSenderAvatars[otherUserId];
         }
      }

      return undefined;
   }, [
      chatRoomAvatar,
      room.avatarUrl,
      room.isPrivate,
      room.members,
      currentUserId,
      messageSenderAvatars,
   ]);

   const chatHeaderAvatar = getChatHeaderAvatar();

   // Get the appropriate display name for the chat header
   const getChatHeaderDisplayName = useCallback(() => {
      const isDirectMessage = room.isPrivate && room.members.length === 2;

      // For direct messages, prioritize showing the other user's name
      if (isDirectMessage) {
         const otherUserId = room.members.find((id) => id !== currentUserId);
         if (otherUserId) {
            // Get messages for this channel to help with name resolution
            const channelMessages = messages[room.id] || roomMessages || [];

            // Try to get name from any message by this user
            const userMessage = channelMessages.find((msg) => msg.senderId === otherUserId);
            if (userMessage && userMessage.senderName) {
               const senderName = userMessage.senderName;
               if (senderName !== 'Unknown User' && !senderName.startsWith('User ')) {
                  return senderName;
               }
            }

            // Try to get name from the last message if it's from the other user
            if (
               room.lastMessage &&
               room.lastMessage.senderId === otherUserId &&
               room.lastMessage.senderName
            ) {
               const senderName = room.lastMessage.senderName;
               if (senderName !== 'Unknown User' && !senderName.startsWith('User ')) {
                  return senderName;
               }
            }

            // Only use the stored name if it's a valid user name (not auto-generated)
            if (
               room.name &&
               room.name !== 'Unknown Chat' &&
               !room.name.startsWith('Room ') &&
               !room.name.startsWith('Chat with ') &&
               !room.name.includes('Unknown') &&
               room.name !== 'Direct Chat'
            ) {
               return room.name;
            }

            // Fallback to a more user-friendly format
            return `Chat with ${otherUserId.substring(0, 8)}...`;
         }
      }

      // For group chats, use the provided name
      if (
         room.name &&
         room.name !== 'Unknown Chat' &&
         !room.name.startsWith('Room ') &&
         !room.name.startsWith('Chat with ') &&
         !room.name.includes('Unknown')
      ) {
         return room.name;
      }

      // Final fallback
      return room.name || `${room.isPrivate ? 'Direct Chat' : 'Group Chat'}`;
   }, [
      room.name,
      room.isPrivate,
      room.members,
      room.lastMessage,
      currentUserId,
      messages,
      roomMessages,
   ]);

   const chatHeaderDisplayName = getChatHeaderDisplayName();

   // Send message handler
   const handleSendMessage = async () => {
      if (!newMessage.trim() || !room.id || isCurrentlySendingMessage) return;

      try {
         await sendMessage(room.id, newMessage.trim());
         setNewMessage('');
         setError(null);
      } catch (err) {
         console.error('❌ Error sending message:', err);
         setError(err instanceof Error ? err.message : 'Failed to send message');
      }
   };

   // Handle key press for sending messages
   const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
         event.preventDefault();
         handleSendMessage();
      } else if (event.key !== 'Enter') {
         // Send typing indicator
         startTyping(room.id);
         // Stop typing after 1 second of inactivity
         setTimeout(() => stopTyping(room.id), 1000);
      }
   };

   // Handle message reactions
   const handleReaction = async (messageId: string, emoji: string) => {
      try {
         const message = roomMessages.find((msg) => msg.id === messageId);
         if (!message) return;

         const userReactions = message.reactions?.[emoji] || [];
         const hasReacted = userReactions.includes(currentUserId);

         if (hasReacted) {
            // Check if currently removing this reaction
            if (isRemovingReaction(messageId, emoji)) return;
            await removeReaction(messageId, emoji);
         } else {
            // Check if currently adding this reaction
            if (isAddingReaction(messageId, emoji)) return;
            await reactToMessage(messageId, emoji);
         }
      } catch (err) {
         console.error('❌ Error handling reaction:', err);
         setError(err instanceof Error ? err.message : 'Failed to update reaction');
      }
   };

   const getUserDisplayName = (userId: string): string => {
      if (userId === currentUserId) return 'You';
      const userMessage = roomMessages.find((msg) => msg.senderId === userId);
      return userMessage?.senderName || `User ${userId.substring(0, 8)}`;
   };

   // Handle menu actions
   const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchorEl(event.currentTarget);
   };

   const handleMenuClose = () => {
      setMenuAnchorEl(null);
   };

   const handleShowParticipants = async () => {
      setMenuAnchorEl(null);
      setParticipantsModalOpen(true);
      setLoadingParticipants(true);

      try {
         // Fetch participants data with online status
         const participants = await Promise.all(
            room.members.map(async (userId) => {
               try {
                  // Get online status from Firebase first, then use real-time status
                  const userStatus = await onlineStatusService.getUserOnlineStatus(userId);
                  return {
                     userId,
                     displayName: getUserDisplayName(userId),
                     email: userStatus?.username || undefined,
                     photoURL: messageSenderAvatars[userId] ?? undefined,
                  };
               } catch (error) {
                  // Fallback to basic data with real-time status
                  return {
                     userId,
                     displayName: getUserDisplayName(userId),
                     email: undefined,
                     photoURL: messageSenderAvatars[userId] ?? undefined,
                  };
               }
            })
         );

         setParticipantsData(participants);
      } catch (error) {
         console.error('Error fetching participants:', error);
         // Fallback to basic member list with real-time status
         const basicParticipants = room.members.map((userId) => ({
            userId,
            displayName: getUserDisplayName(userId),
            email: undefined,
            photoURL: messageSenderAvatars[userId] ?? undefined,
         }));
         setParticipantsData(basicParticipants);
      } finally {
         setLoadingParticipants(false);
      }
   };

   const handleCloseParticipantsModal = () => {
      setParticipantsModalOpen(false);
      setParticipantsData([]);
   };

   if (loading) {
      return (
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               height: '400px',
            }}
         >
            <CircularProgress />
         </Box>
      );
   }

   return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
         {/* Chat Header */}
         <Paper elevation={1} sx={{ p: 2, borderRadius: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
               <Badge
                  overlap='circular'
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                     room.isPrivate && room.members.length === 2
                        ? (() => {
                             const otherUserId = room.members.find((id) => id !== currentUserId);
                             return otherUserId ? (
                                <OnlineIndicator userId={otherUserId} size='small' />
                             ) : null;
                          })()
                        : null
                  }
               >
                  <Avatar src={chatHeaderAvatar || undefined} sx={{ width: 40, height: 40 }}>
                     {chatHeaderDisplayName?.[0]?.toUpperCase()}
                  </Avatar>
               </Badge>
               <Box sx={{ flexGrow: 1, ml: 2 }}>
                  <Typography variant='h6'>{chatHeaderDisplayName}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     {room.isPrivate && room.members.length === 2 ? (
                        (() => {
                           const otherUserId = room.members.find((id) => id !== currentUserId);
                           return otherUserId ? (
                              <OnlineIndicator userId={otherUserId} showText={true} size='small' />
                           ) : (
                              <Typography variant='body2' color='text.secondary'>
                                 Status unknown
                              </Typography>
                           );
                        })()
                     ) : (
                        <Typography variant='body2' color='text.secondary'>
                           {(() => {
                              // For group chats, use a clean description
                              if (
                                 room.description &&
                                 room.description !== 'Unknown Chat' &&
                                 !room.description.startsWith('Room ') &&
                                 !room.description.startsWith('Chat with ') &&
                                 !room.description.includes('Unknown')
                              ) {
                                 return room.description;
                              }
                              // Show member count for group chats
                              return `${room.members.length} members`;
                           })()}
                        </Typography>
                     )}
                  </Box>
               </Box>

               <Tooltip title='Chat options'>
                  <IconButton onClick={handleMenuClick} size='small'>
                     <MoreVertIcon />
                  </IconButton>
               </Tooltip>
            </Box>

            {/* Dropdown Menu */}
            <Menu
               anchorEl={menuAnchorEl}
               open={menuOpen}
               onClose={handleMenuClose}
               PaperProps={{
                  elevation: 3,
                  sx: {
                     mt: 1,
                     minWidth: 160,
                  },
               }}
               transformOrigin={{ horizontal: 'right', vertical: 'top' }}
               anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
               <MenuItem onClick={handleShowParticipants}>
                  <PeopleIcon sx={{ mr: 2 }} />
                  Show Participants
               </MenuItem>
            </Menu>
         </Paper>

         {/* Error Alert */}
         {error && (
            <Alert severity='error' onClose={() => setError(null)} sx={{ m: 1 }}>
               {error}
            </Alert>
         )}

         {/* Messages List */}
         <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
            <List>
               {memoizedMessages
                  .filter((message, index, array) => {
                     // Remove any potential duplicates based on message ID
                     return array.findIndex((m) => m.id === message.id) === index;
                  })
                  .map((message) => {
                     const isOwnMessage = message.senderId === currentUserId;
                     const senderAvatarUrl = messageSenderAvatars[message.senderId] || undefined;

                     return (
                        <Box
                           key={message.id}
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                              mb: 3,
                              maxWidth: '100%',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'flex-end',
                                 maxWidth: '75%',
                                 minWidth: '200px',
                                 gap: 1,
                              }}
                           >
                              {!isOwnMessage && (
                                 <Avatar
                                    src={senderAvatarUrl}
                                    sx={{
                                       width: 36,
                                       height: 36,
                                       mb: 0.5,
                                       border: '2px solid',
                                       borderColor: 'background.paper',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                 >
                                    {message.senderName?.[0]?.toUpperCase() || 'U'}
                                 </Avatar>
                              )}

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                 {/* Reaction Buttons - Show for all messages */}
                                 <Box
                                    sx={{
                                       mb: 0.5,
                                       display: 'flex',
                                       gap: 0.25,
                                       justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                                    }}
                                 >
                                    <IconButton
                                       size='small'
                                       onClick={() => handleReaction(message.id, '👍')}
                                       disabled={
                                          isAddingReaction(message.id, '👍') ||
                                          isRemovingReaction(message.id, '👍')
                                       }
                                       sx={{
                                          width: 20,
                                          height: 20,
                                          color: message.reactions?.['👍']?.includes(currentUserId)
                                             ? 'primary.main'
                                             : 'text.secondary',
                                          '&:hover': {
                                             transform: 'scale(1.2)',
                                          },
                                          transition: 'all 0.2s ease',
                                          opacity:
                                             isAddingReaction(message.id, '👍') ||
                                             isRemovingReaction(message.id, '👍')
                                                ? 0.6
                                                : 1,
                                       }}
                                    >
                                       {isAddingReaction(message.id, '👍') ||
                                       isRemovingReaction(message.id, '👍') ? (
                                          <CircularProgress size={20} />
                                       ) : (
                                          <ThumbUpIcon sx={{ fontSize: 12 }} />
                                       )}
                                    </IconButton>
                                    <IconButton
                                       size='small'
                                       onClick={() => handleReaction(message.id, '❤️')}
                                       disabled={
                                          isAddingReaction(message.id, '❤️') ||
                                          isRemovingReaction(message.id, '❤️')
                                       }
                                       sx={{
                                          width: 20,
                                          height: 20,
                                          color: message.reactions?.['❤️']?.includes(currentUserId)
                                             ? 'error.main'
                                             : 'text.secondary',
                                          '&:hover': {
                                             transform: 'scale(1.2)',
                                          },
                                          transition: 'all 0.2s ease',
                                          opacity:
                                             isAddingReaction(message.id, '❤️') ||
                                             isRemovingReaction(message.id, '❤️')
                                                ? 0.6
                                                : 1,
                                       }}
                                    >
                                       {isAddingReaction(message.id, '❤️') ||
                                       isRemovingReaction(message.id, '❤️') ? (
                                          <CircularProgress size={20} />
                                       ) : (
                                          <FavoriteIcon sx={{ fontSize: 12 }} />
                                       )}
                                    </IconButton>
                                    <IconButton
                                       size='small'
                                       onClick={() => handleReaction(message.id, '😂')}
                                       disabled={
                                          isAddingReaction(message.id, '😂') ||
                                          isRemovingReaction(message.id, '😂')
                                       }
                                       sx={{
                                          width: 20,
                                          height: 20,
                                          color: message.reactions?.['😂']?.includes(currentUserId)
                                             ? 'warning.main'
                                             : 'text.secondary',
                                          '&:hover': {
                                             transform: 'scale(1.2)',
                                          },
                                          transition: 'all 0.2s ease',
                                          opacity:
                                             isAddingReaction(message.id, '😂') ||
                                             isRemovingReaction(message.id, '😂')
                                                ? 0.6
                                                : 1,
                                       }}
                                    >
                                       {isAddingReaction(message.id, '😂') ||
                                       isRemovingReaction(message.id, '😂') ? (
                                          <CircularProgress size={20} />
                                       ) : (
                                          <LaughIcon sx={{ fontSize: 12 }} />
                                       )}
                                    </IconButton>
                                 </Box>

                                 <Paper
                                    elevation={0}
                                    sx={{
                                       px: 2,
                                       py: 1.5,
                                       maxWidth: '100%',
                                       bgcolor: isOwnMessage ? 'primary.main' : 'grey.50',
                                       color: isOwnMessage
                                          ? 'primary.contrastText'
                                          : 'text.primary',
                                       borderRadius: isOwnMessage
                                          ? '20px 20px 4px 20px'
                                          : '20px 20px 20px 4px',
                                       position: 'relative',
                                       border: '1px solid',
                                       borderColor: isOwnMessage ? 'primary.dark' : 'grey.200',
                                       boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                       transition: 'all 0.2s ease',
                                       '&:hover': {
                                          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                          transform: 'translateY(-1px)',
                                       },
                                    }}
                                 >
                                    {/* Sender name for group chats */}
                                    {!isOwnMessage && !room.isPrivate && (
                                       <Typography
                                          variant='caption'
                                          sx={{
                                             fontWeight: 600,
                                             color: 'primary.main',
                                             display: 'block',
                                             mb: 0.5,
                                          }}
                                       >
                                          {message.senderName}
                                       </Typography>
                                    )}

                                    {/* Message Text */}
                                    <Typography
                                       variant='body1'
                                       sx={{
                                          wordBreak: 'break-word',
                                          lineHeight: 1.4,
                                          fontSize: '0.95rem',
                                       }}
                                    >
                                       {message.text}
                                    </Typography>
                                 </Paper>

                                 {/* Reactions Display */}
                                 {message.reactions &&
                                    Object.keys(message.reactions).length > 0 && (
                                       <Box
                                          sx={{
                                             mt: 0.5,
                                             display: 'flex',
                                             flexWrap: 'wrap',
                                             gap: 0.25,
                                          }}
                                       >
                                          {Object.entries(message.reactions || {}).map(
                                             ([emoji, users]) =>
                                                users.length > 0 ? (
                                                   <Chip
                                                      key={emoji}
                                                      label={`${emoji} ${users.length}`}
                                                      size='small'
                                                      onClick={() =>
                                                         handleReaction(message.id, emoji)
                                                      }
                                                      disabled={
                                                         isAddingReaction(message.id, emoji) ||
                                                         isRemovingReaction(message.id, emoji)
                                                      }
                                                      color={
                                                         users.includes(currentUserId)
                                                            ? 'primary'
                                                            : 'default'
                                                      }
                                                      variant={
                                                         users.includes(currentUserId)
                                                            ? 'filled'
                                                            : 'outlined'
                                                      }
                                                      sx={{
                                                         fontSize: '0.7rem',
                                                         cursor: 'pointer',
                                                         '&:hover': {
                                                            backgroundColor: users.includes(
                                                               currentUserId
                                                            )
                                                               ? 'primary.dark'
                                                               : 'action.hover',
                                                         },
                                                         opacity:
                                                            isAddingReaction(message.id, emoji) ||
                                                            isRemovingReaction(message.id, emoji)
                                                               ? 0.6
                                                               : 1,
                                                      }}
                                                   />
                                                ) : null
                                          )}
                                       </Box>
                                    )}
                              </Box>

                              {isOwnMessage && (
                                 <Avatar
                                    src={senderAvatarUrl}
                                    sx={{
                                       width: 36,
                                       height: 36,
                                       mb: 0.5,
                                       border: '2px solid',
                                       borderColor: 'primary.main',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                 >
                                    {message.senderName?.[0]?.toUpperCase() || 'U'}
                                 </Avatar>
                              )}
                           </Box>

                           {/* Message timestamp */}
                           <Typography
                              variant='caption'
                              sx={{
                                 mt: 0.5,
                                 mx: isOwnMessage ? 5 : 5,
                                 color: 'text.secondary',
                                 fontSize: '0.7rem',
                                 fontWeight: 500,
                                 textAlign: isOwnMessage ? 'right' : 'left',
                              }}
                           >
                              {getUserDisplayName(message.senderId)} •{' '}
                              {formatDistanceToNow(message.timestamp, { addSuffix: true })}
                              {message.edited && (
                                 <Typography
                                    component='span'
                                    sx={{
                                       ml: 0.5,
                                       fontStyle: 'italic',
                                       opacity: 0.7,
                                    }}
                                 >
                                    (edited)
                                 </Typography>
                              )}
                           </Typography>
                        </Box>
                     );
                  })}
            </List>
            <div ref={messagesEndRef} />
         </Box>

         {/* Message Input */}
         <Paper elevation={1} sx={{ p: 2, borderRadius: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
               <TextField
                  fullWidth
                  variant='outlined'
                  placeholder='Type your message...'
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={isCurrentlySendingMessage}
                  size='small'
                  InputProps={{
                     endAdornment: (
                        <InputAdornment position='end'>
                           <IconButton
                              onClick={handleSendMessage}
                              disabled={!newMessage.trim() || isCurrentlySendingMessage}
                              color='primary'
                              size='small'
                           >
                              {isCurrentlySendingMessage ? (
                                 <CircularProgress size={20} />
                              ) : (
                                 <SendIcon />
                              )}
                           </IconButton>
                        </InputAdornment>
                     ),
                  }}
               />
            </Box>
         </Paper>

         {/* Typing indicators */}
         {typingUsers[room.id] && typingUsers[room.id].length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
               <Typography variant='body2' color='text.secondary'>
                  {typingUsers[room.id]
                     .filter((userId) => userId !== currentUserId)
                     .map((userId) => getUserDisplayName(userId))
                     .join(', ')}{' '}
                  {typingUsers[room.id].filter((id) => id !== currentUserId).length === 1
                     ? 'is'
                     : 'are'}{' '}
                  typing...
               </Typography>
            </Box>
         )}

         {/* Participants Modal */}
         <Dialog
            open={participantsModalOpen}
            onClose={handleCloseParticipantsModal}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  <Typography variant='h6'>Participants ({room.members.length})</Typography>
               </Box>
            </DialogTitle>
            <DialogContent>
               {loadingParticipants ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                     <CircularProgress />
                  </Box>
               ) : (
                  <List>
                     {participantsData.map((participant) => (
                        <ListItem key={participant.userId} divider>
                           <ListItemAvatar>
                              <Badge
                                 overlap='circular'
                                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                 badgeContent={
                                    <OnlineIndicator userId={participant.userId} size='small' />
                                 }
                              >
                                 <Avatar src={participant.photoURL} sx={{ width: 40, height: 40 }}>
                                    {participant.displayName[0]?.toUpperCase() || 'U'}
                                 </Avatar>
                              </Badge>
                           </ListItemAvatar>
                           <ListItemText
                              primary={participant.displayName}
                              secondary={
                                 <Box>
                                    <Typography variant='body2' color='text.secondary'>
                                       {participant.email}
                                    </Typography>
                                    <OnlineIndicator
                                       userId={participant.userId}
                                       showText={true}
                                       size='small'
                                    />
                                 </Box>
                              }
                           />
                           {participant.userId === currentUserId && (
                              <Chip label='You' size='small' color='primary' />
                           )}
                        </ListItem>
                     ))}
                  </List>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseParticipantsModal}>Close</Button>
            </DialogActions>
         </Dialog>
      </Box>
   );
};

export default WebSocketChatView;
